/**
 * Text der Kontextzeile über der Abschnittsüberschrift des Immobilien-Risikos.
 *
 * Sie ersetzt die frühere Formular-Kopfzeile („Zurück" + großer Titel): statt
 * einer zweiten Überschrift steht dort ruhig und klein, WORAN gerade gearbeitet
 * wird — Objektart, Anschrift, Kunde.
 *
 * Vier Formen, je nachdem was belegt ist:
 *   Immobilie · <Anschrift> — <Kundenname>
 *   Immobilie — <Kundenname>
 *   Immobilie · <Anschrift>
 *   Immobilie
 *
 * Bewusst eine reine Funktion ohne React: die Antwort-Keys (`street`,
 * `postalCode`, `city`) stehen so in gespeicherten Merkmalen echter Kunden —
 * das Verhalten bei leeren/teilbefüllten Adressen gehört getestet, nicht
 * geraten.
 */

/** Trimmt alles, was kein belegter String ist, auf "" herunter. */
const alsText = (wert: unknown): string =>
  typeof wert === "string" ? wert.trim() : "";

export type AnschriftTeile = {
  /** Trägt bereits die Hausnummer („Straße & Hausnummer" im Anschrift-Block). */
  street?: unknown;
  postalCode?: unknown;
  city?: unknown;
};

/**
 * Kurzform der Objektadresse: `Straße & Hausnummer, PLZ Ort`. Jeder Teil fällt
 * weg, solange er leer ist — auch PLZ ohne Ort und Ort ohne PLZ ergeben eine
 * saubere Zeile.
 */
export const anschriftText = ({ street, postalCode, city }: AnschriftTeile): string => {
  const ort = [alsText(postalCode), alsText(city)].filter(Boolean).join(" ");
  return [alsText(street), ort].filter(Boolean).join(", ");
};

export type KontextZeileTeile = AnschriftTeile & {
  /**
   * Nur im eingebetteten Backoffice-Weg gesetzt. Im öffentlichen
   * Kundenformular zeigt die TopBar den Namen bereits — dort bliebe er hier
   * eine Dopplung.
   */
  kundenName?: string | null;
};

export const kontextZeileText = ({
  street,
  postalCode,
  city,
  kundenName,
}: KontextZeileTeile): string => {
  const anschrift = anschriftText({ street, postalCode, city });
  const name = alsText(kundenName);
  let text = "Immobilie";
  if (anschrift) text += ` · ${anschrift}`;
  if (name) text += ` — ${name}`;
  return text;
};
